import { saveBlob } from "./download.js";
import { env } from "./env.js";

/**
 * THE PORTAL NEVER DRAWS A SALES DOCUMENT (2026-09-17).
 *
 * Every invoice and quote PDF a reseller sees — on screen or saved to disk —
 * is rendered by crm-api's `InvoicePdfRenderer` (xop-brain), the ONE document
 * generator in the stack, and fetched here as bytes. There used to be three:
 * the command center drew its PDFs in the browser with jsPDF, this portal
 * carried a fork of that file, and crm-api had a Ruby renderer for mail. The
 * same invoice therefore looked different depending on which screen it came
 * from. The CEO's ruling: the reseller portal no longer generates invoices, it
 * only displays the document XOP sends. So nothing in this app builds a PDF
 * from row data any more — this file fetches, the shared `<PdfReader>` paints
 * (pdf.js onto a canvas, never an iframe), and `saveBlob` saves.
 *
 * TWO ADDRESSES, one renderer:
 *
 *   invoice or quote row   `GET /api/v1/invoices/:id/pdf` — the reseller-
 *                          granted twin of `#show` (`invoices:view`), 404 when
 *                          crm-api's wall hides the row.
 *   XOP quote (XQ-…)       `GET /api/v1/reseller_quotes/:id/pdf` — the CRM copy
 *                          of the quote (`ResellerQuotes::CrmMirror`) rendered
 *                          by the same renderer. That copy is deliberately
 *                          HIDDEN from reseller actors on the invoices index
 *                          (`Invoice` default scope, `reseller_quote_id: nil`),
 *                          so the invoice address cannot reach it; the quote's
 *                          own address authorises through the quote.
 *                          ⚠ Needs crm-api to add that member route — see
 *                          docs/quotes-and-invoices.md → "One generator".
 *
 * Both ride the BFF's `/api/crm/v1/*` byte passthrough, which forwards the
 * content type and `Content-Disposition` untouched (apps/server crmProxy.ts).
 */

const CRM = "/api/crm/v1";

/** A rendered document: its bytes, and the name crm-api gave it (null when
 * the response carried no usable `Content-Disposition`). */
export interface FetchedPdf {
  bytes: ArrayBuffer;
  filename: string | null;
}

export function documentPdfUrl(id: number | string): string {
  return `${env.apiUrl}${CRM}/invoices/${encodeURIComponent(String(id))}/pdf`;
}

export function xopQuotePdfUrl(id: number | string): string {
  return `${env.apiUrl}${CRM}/reseller_quotes/${encodeURIComponent(String(id))}/pdf`;
}

/**
 * The filename out of a `Content-Disposition` header.
 *
 * Rails' `send_data` writes both forms — `filename="Invoice Acme 901.pdf";
 * filename*=UTF-8''Invoice%20Acme%20901.pdf` — and the RFC 5987 one wins when
 * it decodes, because it is the only one that can carry a non-ASCII company
 * name intact. Null for a missing header or one that names no file.
 */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended?.[2]) {
    try {
      const decoded = decodeURIComponent(extended[2].trim().replace(/^"|"$/g, "")).trim();
      if (decoded) return decoded;
    } catch {
      /* malformed percent-encoding — fall through to the plain form */
    }
  }

  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header);
  if (quoted?.[1] !== undefined) {
    const name = quoted[1].replace(/\\(.)/g, "$1").trim();
    return name || null;
  }

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  const name = bare?.[1]?.trim();
  return name ? name : null;
}

/**
 * GET a rendered PDF through this origin.
 *
 * Throws the BFF's sentence on a non-2xx, and refuses a 2xx that is not a PDF
 * — an HTML page handed to pdf.js would only surface as an unexplained
 * "couldn't preview" card, and saved to disk as a `.pdf` it is worse.
 * FRESH BYTES EVERY CALL: `<PdfReader>` transfers the buffer it is given, so a
 * Download must fetch its own rather than reuse the preview's.
 */
export async function fetchPdf(url: string): Promise<FetchedPdf> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      if (body.message) message = body.message;
      else if (body.error) message = body.error;
    } catch {
      /* non-JSON error body — keep the status */
    }
    throw new Error(message);
  }
  const contentType = res.headers.get("content-type");
  if (contentType && !/^application\/pdf\b/i.test(contentType.trim())) {
    throw new Error("The server did not return a PDF.");
  }
  return {
    bytes: await res.arrayBuffer(),
    filename: filenameFromContentDisposition(res.headers.get("content-disposition")),
  };
}

/** Fetch `url` and save it under crm-api's name, or `fallbackFilename`. */
export async function downloadPdf(url: string, fallbackFilename: string): Promise<void> {
  const { bytes, filename } = await fetchPdf(url);
  await saveBlob(filename ?? fallbackFilename, new Blob([bytes], { type: "application/pdf" }));
}

/** An invoice or quote row (`invoices` table), as crm-api renders it. */
export const fetchDocumentPdf = (id: number | string): Promise<FetchedPdf> => fetchPdf(documentPdfUrl(id));

export const downloadDocumentPdf = (id: number | string, fallbackFilename: string): Promise<void> =>
  downloadPdf(documentPdfUrl(id), fallbackFilename);

/** An XOP quote to this reseller (`reseller_quotes`), as crm-api renders it. */
export const fetchXopQuotePdf = (id: number | string): Promise<FetchedPdf> => fetchPdf(xopQuotePdfUrl(id));

export const downloadXopQuotePdf = (id: number | string, fallbackFilename: string): Promise<void> =>
  downloadPdf(xopQuotePdfUrl(id), fallbackFilename);
